import { MdLaunch } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useReturnDetails } from 'pages/exchange-and-return/hooks';
import { Accordion, Button, CopyButton, Loading, Text } from 'components';
import { formatMillisecondsToDays } from 'helpers';
import { FontSize, FontWeight } from 'commons';
import { EntrySummary } from '../../../components';
import * as S from '../../styles';

interface Props {
  setShowStepReceived: (showStepReceived: boolean) => void;
}

export function WaitingProducts({ setShowStepReceived }: Props) {
  const { returnDetails, consignmentDetails, renewPostCodeExpirationDate } =
    useReturnDetails();

  if (!returnDetails || !consignmentDetails) {
    return <Loading />;
  }

  const { entries, postOffice, consignmentCode } = returnDetails;
  const { customer, assignedPOS } = consignmentDetails;

  const currentDate = new Date().getTime();
  const postOfficeDeadline = postOffice.deadLine.getTime();
  const shouldRevalidate = postOfficeDeadline < currentDate;
  const postOfficeDeadlineFormatted =
    postOffice.deadLine.toLocaleDateString('pt-br');

  function sendWhatsAppMessage() {
    const customerNumber = customer.cellphone || customer.phone;
    const date1 = new Date().getTime();
    const date2 = postOffice.deadLine.getTime();

    if (date1 > date2) {
      return toast.warn('Validade do código de envio expirada');
    }

    const diffDays = formatMillisecondsToDays(date2 - date1).days;

    const entriesText =
      entries.length === 1
        ? `Você pode nos enviar o produto ${entries[0].entry.product.name}, tamanho ${entries[0].entry.product.size} `
        : `Você pode nos enviar os produtos:\n${entries
            .map(({ entry }) => {
              const { product } = entry;
              return ` • ${product.name}, tamanho ${product.size}\n`;
            })
            .join('')}`;

    const text = encodeURI(
      `*${assignedPOS.displayName}*:\nOlá${
        customer.firstName ? ` ${customer.firstName}` : ''
      }, tudo bem? Estou passando pra avisar que a solicitação de devolução do seu pedido *${consignmentCode}* acabou de ser aceita. 🚚\n${entriesText}pelos Correios utilizando o código postal *${
        postOffice.code
      }*\n\n*LEMBRETE:*\nO código postal é válido por ${diffDays} ${
        diffDays === '1' ? 'dia' : 'dias'
      }.`
    );

    const waUrl = `https://api.whatsapp.com/send?phone=+${customerNumber}&text=${text}`;

    window.open(waUrl, '_blanck');
  }

  return (
    <Accordion title="Solicitação de devolução" defaultOpen>
      <S.Main>
        <div>
          <Text size={FontSize.font12}>
            Código de postagem gerado com sucesso, você pode enviar para o
            cliente clicando abaixo para compartilhar com o WhatsApp.
          </Text>
          <Text size={FontSize.font12}>
            O tracking será exibido após o produto ser postado.
          </Text>
        </div>

        {entries.map((summaryEntry, index) => (
          <EntrySummary key={index} summaryEntry={summaryEntry} />
        ))}

        <S.PostWrapper $isColumn>
          <S.PostAction>
            <S.PostCodeWrapper>
              <Text size={FontSize.font12}>Código de postagem:</Text>

              <Text weight={FontWeight.bold}>{postOffice.code}</Text>

              <CopyButton content={postOffice.code}></CopyButton>
            </S.PostCodeWrapper>

            <S.PostRenew>
              <Text size={FontSize.font12}>
                Código válido até {postOfficeDeadlineFormatted}
              </Text>
              <S.ButtonLink
                disabled={!shouldRevalidate}
                onClick={renewPostCodeExpirationDate}
              >
                <Text size={FontSize.font12}>Reativar Código</Text>
              </S.ButtonLink>
            </S.PostRenew>
          </S.PostAction>

          <S.PostInfoWrapper $isRow>
            <Text size={FontSize.font12}>Compartilhe via Whatsapp:</Text>

            <S.ButtonLink onClick={sendWhatsAppMessage}>
              <Text size={FontSize.font12} weight={FontWeight.bold}>
                Abrir whatsapp
              </Text>

              <MdLaunch />
            </S.ButtonLink>
          </S.PostInfoWrapper>
        </S.PostWrapper>

        <S.ButtonWrapper>
          <Button
            variant="secondary"
            onClick={() => {
              setShowStepReceived(true);
            }}
          >
            Cliente entregou na loja
          </Button>
        </S.ButtonWrapper>
      </S.Main>
    </Accordion>
  );
}
